import DownloadIcon from "@mui/icons-material/Download";
import Alert from "@mui/joy/Alert";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import Card from "@mui/joy/Card";
import CardActions from "@mui/joy/CardActions";
import CardContent from "@mui/joy/CardContent";
import Chip from "@mui/joy/Chip";
import CircularProgress from "@mui/joy/CircularProgress";
import DialogContent from "@mui/joy/DialogContent";
import DialogTitle from "@mui/joy/DialogTitle";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import Modal from "@mui/joy/Modal";
import ModalClose from "@mui/joy/ModalClose";
import ModalDialog from "@mui/joy/ModalDialog";
import Option from "@mui/joy/Option";
import Select from "@mui/joy/Select";
import Stack from "@mui/joy/Stack";
import Typography from "@mui/joy/Typography";
import React from "react";
import {
  OmopQuartzClientContext,
  QuestionAnsweringClientContext,
  TaskQuartzClientContext,
} from "../../../shared/services/api/contexts";
import { useSnackbar } from "../../../shared/components/layout/SnackbarContext";
import { ExportFormat } from "../../../models/cohort";
import { Questionnaire } from "../../../models/questionnaire";
import { Participant } from "../../../models/study";
import { StudyContext } from "../pages/StudyPage";
import AnswerTable from "../../../shared/components/export/AnswerTable";

interface ExportTabProps {
  readonly participants: Participant[];
  readonly questionnaire: Questionnaire | null;
  readonly onQuestionnaireEdit: () => void;
}

export default function ExportTab({
  participants,
  questionnaire,
  onQuestionnaireEdit,
}: ExportTabProps) {
  const { showSnackbar } = useSnackbar();
  const study = React.useContext(StudyContext);
  const questionAnsweringClient = React.useContext(QuestionAnsweringClientContext);
  const taskQuartzClient = React.useContext(TaskQuartzClientContext);
  const omopClient = React.useContext(OmopQuartzClientContext);

  const [databaseOption, setDatabaseOption] = React.useState<"unverified" | "verified">(
    "unverified"
  );
  const [exportFormat, setExportFormat] = React.useState<ExportFormat>(ExportFormat.CSV);
  const [openDataExport, setOpenDataExport] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [questionsAnswers, setQuestionsAnswers] = React.useState<any[]>([]);

  const [exportLoading, setExportLoading] = React.useState(false);

  const personIds = participants.map((participant) => participant.person_id);

  async function askQuestions() {
    if (!questionnaire) return;
    try {
      setLoading(true);
      let task = await questionAnsweringClient.askQuestions(questionnaire.item, personIds);
      let taskCompleted = false;
      while (!taskCompleted) {
        try {
          task = await taskQuartzClient.getTaskById(task.task_id);
          if (task.status === "completed") {
            const results = task.output;
            setQuestionsAnswers(results);
            taskCompleted = true;
          } else if (task.status === "failed") {
            showSnackbar({ text: "Task failed", color: "danger" });
            taskCompleted = true;
          }
        } catch (e: any) {
          showSnackbar({ text: e.message, color: "danger" });
          taskCompleted = true;
        }
        await new Promise((resolve) => setTimeout(resolve, 1000)); // Poll every second
      }
    } catch (e: any) {
      showSnackbar({ text: e.message, color: "danger" });
    } finally {
      setLoading(false);
    }
  }

  async function handlePreBuildDownload(format: ExportFormat, dbOption: "unverified" | "verified") {
    setExportLoading(true);
    try {
      await omopClient.exportOmop(format, dbOption, undefined, study.study_id);
    } catch (e: any) {
      showSnackbar({ text: e.message, color: "danger" });
    } finally {
      setExportLoading(false);
    }
  }

  const handlePersonalizedPreview = () => {
    setOpenDataExport(true);
    if (questionnaire) {
      askQuestions();
    }
  };

  async function handlePersonalizedDownload() {
    try {
      setExportLoading(true);
      await questionAnsweringClient.exportQuestionAnswers(questionsAnswers);
    } catch (e: any) {
      showSnackbar({ text: e.message, color: "danger" });
    } finally {
      setExportLoading(false);
    }
  }

  return (
    <Box>
      <Alert variant="soft" color="primary" sx={{ mb: 2 }}>
        Only participants who have been enrolled in the project will be included in the export.
      </Alert>
      {study.acronym && study.acronym === "ODH" ? (
        <Stack spacing={2} sx={{ maxWidth: "60ch" }}>
          <Card>
            <CardContent>
              <Typography level="title-lg">Pre-build export for OncoDataHub</Typography>
              <Typography level="body-md">
                A predefined export option is available for OncoDataHub.
              </Typography>
            </CardContent>
            <CardActions buttonFlex="0 1 190px">
              <Button
                variant="solid"
                color="primary"
                loading={exportLoading}
                startDecorator={<DownloadIcon />}
                onClick={() => {
                  handlePreBuildDownload(ExportFormat.ODH, "unverified");
                }}
              >
                Download
              </Button>
            </CardActions>
          </Card>
        </Stack>
      ) : (
        <Stack spacing={2} sx={{ maxWidth: "60ch" }}>
          <Card>
            <CardContent>
              <Typography level="title-lg">
                Pre-build export in{" "}
                <Typography
                  level="title-lg"
                  textColor="var(--joy-palette-success-plainColor)"
                  sx={{ fontFamily: "monospace", opacity: "50%" }}
                >
                  OMOP
                </Typography>
              </Typography>
              <Typography level="body-md">
                Download the pre-build export in OMOP format (v 5.4).
              </Typography>
              <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                <FormControl>
                  <FormLabel>Database</FormLabel>
                  <Select
                    placeholder="Select database"
                    value={databaseOption}
                    onChange={(event, newValue) => setDatabaseOption(newValue || "unverified")}
                    sx={{ minWidth: 200 }}
                  >
                    <Option value="unverified">All facts</Option>
                    <Option value="verified">Validated facts only</Option>
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel>Format</FormLabel>
                  <Select
                    placeholder="Export format"
                    value={exportFormat}
                    onChange={(event, newValue) => setExportFormat(newValue || ExportFormat.CSV)}
                    sx={{ minWidth: 200 }}
                  >
                    <Option value={ExportFormat.CSV}>CSV</Option>
                    <Option value={ExportFormat.SQLITE}>SQLite</Option>
                  </Select>
                </FormControl>
              </Stack>
            </CardContent>
            <CardActions buttonFlex="0 1 150px">
              <Button
                variant="solid"
                color="primary"
                loading={exportLoading}
                disabled={exportLoading}
                onClick={() => {
                  handlePreBuildDownload(exportFormat, databaseOption);
                }}
                startDecorator={<DownloadIcon />}
              >
                Download
              </Button>
            </CardActions>
          </Card>
          <Card>
            <CardContent>
              <Typography level="title-lg">Personalized export</Typography>
              <Typography level="body-md">
                Select the concepts of interest, view the exported data and download it in Excel
                format.
              </Typography>
              <Stack spacing={2} direction="row" alignItems="center">
                {questionnaire && (
                  <Chip size="sm" variant="soft" color="primary">
                    {questionnaire.item.length} question{questionnaire.item.length > 1 ? "s" : ""}{" "}
                    selected
                  </Chip>
                )}
              </Stack>
            </CardContent>
            <CardActions buttonFlex="0 1 190px">
              <Button variant="outlined" color="neutral" onClick={onQuestionnaireEdit}>
                Manage questions
              </Button>
              <Button
                variant="outlined"
                color="neutral"
                onClick={handlePersonalizedPreview}
                disabled={!questionnaire || questionnaire.item.length === 0}
              >
                Open data explorer
              </Button>
            </CardActions>
            {questionnaire && questionnaire.item.length !== 0 && questionsAnswers.length !== 0 && (
              <CardActions buttonFlex="0 1 150px">
                <Button
                  variant="solid"
                  color="primary"
                  startDecorator={<DownloadIcon />}
                  onClick={handlePersonalizedDownload}
                  loading={exportLoading}
                >
                  Download
                </Button>
              </CardActions>
            )}
          </Card>
        </Stack>
      )}

      <Modal open={!!openDataExport} onClose={() => setOpenDataExport(false)}>
        <ModalDialog layout="fullscreen">
          <ModalClose />
          <DialogTitle sx={{ mb: 2 }}>Data explorer</DialogTitle>
          <DialogContent>
            {loading ? (
              <CircularProgress size="lg" />
            ) : (
              <AnswerTable
                questionsAnswers={questionsAnswers}
                personIds={personIds}
                handleRemove={() => {}}
              />
            )}
          </DialogContent>
        </ModalDialog>
      </Modal>
    </Box>
  );
}
